/* eslint-env browser */

// Do not require files here unless we are enforcing that they stay slim as
// far as things brought in. Anything nontrivial in size, even something like
// moment, should not be brought in.
const ElevShared = require("../../../../shared/ElevShared");
const ElevPrices = require("./ElevPrices");
const {
	AJAX_TIMEOUT,
	runAjax,
	$each,
	$find,
	toggleClass,
	onClickOrEnter,
	compileTemplate,
	toggleAttribute,
	getFocusableElements,
	setupToggleOn,
	hasClass,
	scrollToEl,
	toggleExpandableOpen,
	logMarketingSource,
	sendDataLayerEvent,
	getCookie,
	disableLiveClassesLinks,
} = require("./ElevHelpers");
const {runAjaxAuthenticate} = require("./LoginUtil");
const {
	modifyCart,
	addItemsToCart,
	showAddToCartModal,
	getTotalQuantity,
	getDefaultCart,
	loadCartFromStorage,
	saveCartToStorage,
} = require("./CartUtil");
const {openModal, openModalByName, setupModalButton} = require("./ModalUtil");
const {setupTopBar, setupTopBarPostAuth} = require("./TopBarUtil");
const {initSubscribe} = require("./EmailSubscribe");
const {
	setupBookBuy,
	setupBookBuyPostAuth,
	setupBookBuyForClassWallet,
} = require("./BookUtil");
const {setupAllBooks} = require("./AllBooksUtil");
const {setupSaveWithFriends} = require("./SaveWithFriendsUtil");
const {setupGiftingPage} = require("./GiftingUtil");
const {setupEnrollPage} = require("./EnrollUtil");
const {setupPlayground} = require("./PlaygroundUtil");
const {setupOutcomesPage} = require("./OutcomesUtil");
const {setupContactUsPage, setupContactPagePostAuth} = require("./ContactUtil");
const {track} = require("../../../core/util/Mixpanel");

//
// Polyfills
//

if (!("remove" in Element.prototype)) {
	Element.prototype.remove = function() {
		if (this.parentNode) {
			this.parentNode.removeChild(this);
		}
	};
}

//
// Resize listening
//

function debounce(f, delay) {
	let timeout = null;
	const debouncedF = function(...args) {
		if (timeout) clearTimeout(timeout);
		timeout = setTimeout(() => {
			f(...args);
		}, delay);
	};
	return debouncedF;
}

const WINDOW_RESIZE_DEBOUNCE_DELAY = 200;

// null means no resize listener registered. Set to an array of functions when
// the listener is registered.
let debouncedResizeListeners = null;

// Add the provided function f to a shared debounced listener for window
// resizing. f will be called without arguments.
function onDebouncedResize(f) {
	if (!debouncedResizeListeners) {
		debouncedResizeListeners = [];
		const resizeHandler = debounce(function() {
			debouncedResizeListeners.forEach(function(g) {
				g();
			});
		}, WINDOW_RESIZE_DEBOUNCE_DELAY);
		window.addEventListener("resize", resizeHandler);
	}
	debouncedResizeListeners.push(f);
}

// Remove a previously added resize listener.
// eslint-disable-next-line no-unused-vars
function offDebouncedResize(f) {
	if (!debouncedResizeListeners) return;
	const i = debouncedResizeListeners.indexOf(f);
	if (i > -1) {
		debouncedResizeListeners.splice(i, 1);
	}
}

//
// JS for Imitator Indicator
//

function setupImitatorIndicator(user) {
	if (!user || !user.id || !user.imitatorUserID) return;
	const {imitatorUserID, id} = user;
	const imitatorIndicator = document.body.querySelector("#imitatorIndicator");
	const imitatorIndicatorTemplate = compileTemplate(
		"#imitatorIndicatorTemplate"
	);
	const handlebarsVars = {
		imitateeUserID: id,
		imitatorIdentifier: user.imitatorIdentifier ?? imitatorUserID,
	};
	imitatorIndicator.innerHTML = imitatorIndicatorTemplate(handlebarsVars);
	toggleClass(imitatorIndicator, "ba11--hidden", false);
	const onUnimitateClick = () =>
		runAjax(
			{
				endpoint: "/api/user/unimitate",
				timeout: AJAX_TIMEOUT,
			},
			err => {
				if (!err) {
					window.location = "/admin/accounts";
				} else {
					const unimitateFailed = imitatorIndicator.querySelector(
						".ba11-imitator-unimitate-failed"
					);
					toggleClass(unimitateFailed, "ba11--hidden", false);
					setTimeout(() => {
						toggleClass(unimitateFailed, "ba11--hidden", true);
					}, 10000);
				}
			}
		);
	const unimitateButton = imitatorIndicator.querySelector(
		"button.unimitate-button"
	);
	onClickOrEnter(unimitateButton, onUnimitateClick);
}

//
// JS for carousel
//

const CAROUSEL_CONSTANTS = ElevShared.CAROUSEL_CONSTANTS;

function getCarouselVisibleFromWidth(windowWidth, isNarrower) {
	const cutoffs = CAROUSEL_CONSTANTS.PAGES_VISIBLE_CUTOFFS;
	for (let i = 0; i < cutoffs.length; i += 1) {
		if (windowWidth > cutoffs[i][0])
			return Math.min(isNarrower ? 4 : 5, cutoffs[i][1]);
	}
	return 1;
}

// Function that gets various derived values for the carousel.
function getCarouselDerived(windowWidth, entryCount, currIndex, isNarrower) {
	const C = CAROUSEL_CONSTANTS;
	const widthFromWindow =
		windowWidth - 2 * (C.WINDOW_MARGIN + C.ARROW_SIZE) - C.SCROLL_BAR_WIDTH;
	const maxPageWidth = isNarrower
		? C.MAX_PAGE_WIDTH - C.PAGE_MARGIN
		: C.MAX_PAGE_WIDTH;
	const containerWidth = Math.min(
		C.MAX_WIDTH,
		maxPageWidth * entryCount,
		Math.max(C.MIN_PAGE_WIDTH, widthFromWindow)
	);

	const maxVisible = getCarouselVisibleFromWidth(windowWidth, isNarrower);
	const totalVisible = Math.min(maxVisible, entryCount);
	const firstVisible = Math.max(
		Math.min(currIndex, entryCount - totalVisible),
		0
	);
	const lastVisible = firstVisible + totalVisible - 1;
	const showPrevious = firstVisible > 0;
	const showNext = lastVisible < entryCount - 1;

	const entryWidth = Math.max(
		C.MIN_PAGE_WIDTH - 2 * C.PAGE_MARGIN,
		Math.min(maxPageWidth, containerWidth / totalVisible - 2 * C.PAGE_MARGIN)
	);
	const paddedEntryWidth = entryWidth + 2 * C.PAGE_MARGIN;

	const contentTranslate =
		-(paddedEntryWidth * totalVisible) / 2 - firstVisible * paddedEntryWidth;
	const contentWidth = paddedEntryWidth * entryCount;

	return {
		containerWidth,
		contentWidth,
		totalVisible,
		paddedEntryWidth,
		entryWidth,
		contentTranslate,
		firstVisible,
		lastVisible,
		showPrevious,
		showNext,
	};
}

function setupCarousel(el) {
	// Carousel state.

	// Index of the first page to be visible. Allowed to be too high for current
	// window size so that enlarging and shrinking the window will preserve what
	// is shown.
	let currIndex = 0;

	// Carousel functionality.

	const contentEl = el.querySelector(".ba11-carousel-content");
	const contentInnerEl = el.querySelector(".ba11-carousel-content-inner");
	const entries = $find(el, ".ba11-carousel-entry");
	const prevButton = el.querySelector(".ba11-carousel-arrow-prev");
	const nextButton = el.querySelector(".ba11-carousel-arrow-next");
	const isInsideExpandable = el.parentNode.classList.contains(
		"ba11-expandable-contents"
	);

	function updateCarousel() {
		const windowWidth = window.innerWidth;
		const derived = getCarouselDerived(
			windowWidth,
			entries.length,
			currIndex,
			isInsideExpandable
		);

		contentEl.style.width = derived.containerWidth + "px";
		contentInnerEl.style.transform =
			"translateX(" + derived.contentTranslate + "px)";
		contentInnerEl.style.width = derived.contentWidth + "px";
		prevButton.style.visibility = derived.showPrevious ? "visible" : "hidden";
		nextButton.style.visibility = derived.showNext ? "visible" : "hidden";
		for (let i = 0; i < entries.length; i += 1) {
			const entry = entries[i];
			entry.style.width = derived.entryWidth + "px";
			if (i >= derived.firstVisible && i <= derived.lastVisible) {
				entry.style.opacity = 1;
				entry.style.pointerEvents = "auto";
			} else {
				entry.style.opacity = 0;
				entry.style.pointerEvents = "none";
			}
		}
	}

	function ensureVisible(index) {
		const windowWidth = window.innerWidth;
		const derived = getCarouselDerived(windowWidth, entries.length, currIndex);
		if (index < derived.firstVisible) {
			currIndex = index;
			updateCarousel();
		} else if (index > derived.lastVisible) {
			currIndex += index - derived.lastVisible;
			updateCarousel();
		}
	}

	onDebouncedResize(updateCarousel);
	updateCarousel();

	prevButton.addEventListener("click", function(evt) {
		currIndex -= 1;
		updateCarousel();
	});

	nextButton.addEventListener("click", function(evt) {
		currIndex += 1;
		updateCarousel();
	});

	// Make tab navigation scroll through the carousel.
	entries.forEach(function(entry, i) {
		const link = entry.querySelector("a");
		link &&
			link.addEventListener("focus", function(evt) {
				ensureVisible(i);
			});
	});

	// Undo CSS to hide carousel initially
	el.style.visibility = "visible";
}

//
// JS to fill in price fields dynamically
//
function fillInPrices() {
	const setPriceStr = function(
		el,
		item,
		multi,
		perMonth,
		currencyUnit,
		rounded
	) {
		let itemPrice;
		if (item === "bundle") {
			// TODO - update this once BA1 bundle becomes available
			itemPrice = ElevPrices.getBundlePriceForGrade(2);
		} else if (item === "bundle-undiscounted") {
			itemPrice = ElevPrices.getBundlePriceForGrade(2, true);
		} else if (item === "book-guide-practice-set") {
			itemPrice = ElevPrices.getGuidePracticeBookSetPriceForGrade(2);
		} else if (item === "beast-yearly-plus-multi") {
			itemPrice =
				ElevPrices.getMultiPriceForSku("beast-yearly") +
				ElevPrices.getBaseItemPriceForSku("beast-yearly");
		} else if (item === "bundle-minus-books") {
			itemPrice =
				ElevPrices.getBundlePriceForGrade(2) -
				ElevPrices.getGuidePracticeBookSetPriceForGrade(2);
		} else {
			itemPrice = multi
				? ElevPrices.getMultiPriceForSku(item)
				: ElevPrices.getBaseItemPriceForSku(item);
		}
		if (rounded) {
			itemPrice = Math.ceil(itemPrice);
		}
		if (perMonth) {
			itemPrice = Math.ceil((itemPrice * 100) / 12) / 100;
		}
		itemPrice = ElevPrices.finishPrice(itemPrice);
		let price = itemPrice % 1 ? itemPrice.toFixed(2) : itemPrice;
		if (currencyUnit) {
			if (typeof price === "number") {
				price = itemPrice.toFixed(2);
			}
			const priceSplit = price.split(".");
			if (priceSplit.length >= 2) {
				price = currencyUnit === "dollars" ? priceSplit[0] : priceSplit[1];
			} else {
				price = priceSplit[0];
			}
			if (price === "00") {
				price = "";
			}
		}
		el.innerText = price;
	};
	$each(document, "span.monthly-sub-base-price-insert:not(.text-span)", el =>
		setPriceStr(el, "beast-monthly")
	);
	$each(document, "span.monthly-sub-base-price-insert-dollars", el =>
		setPriceStr(el, "beast-monthly", false, false, "dollars")
	);
	$each(document, "span.monthly-sub-base-price-insert-cents", el =>
		setPriceStr(el, "beast-monthly", false, false, "cents")
	);
	$each(document, "span.monthly-sub-base-price-insert-rounded", el =>
		setPriceStr(el, "beast-monthly", false, false, false, true)
	);
	$each(document, "span.monthly-sub-multi-price-insert", el =>
		setPriceStr(el, "beast-monthly", true)
	);
	$each(document, "span.monthly-sub-multi-price-insert-rounded", el =>
		setPriceStr(el, "beast-monthly", true, false, false, true)
	);
	$each(document, "span.yearly-sub-base-price-insert", el =>
		setPriceStr(el, "beast-yearly")
	);
	$each(document, "span.yearly-sub-base-price-insert-rounded", el =>
		setPriceStr(el, "beast-yearly", false, false, false, true)
	);
	$each(document, "span.yearly-sub-base-price-insert-dollars", el =>
		setPriceStr(el, "beast-yearly", false, false, "dollars")
	);
	$each(document, "span.yearly-sub-base-price-insert-cents", el =>
		setPriceStr(el, "beast-yearly", false, false, "cents")
	);
	$each(document, "span.yearly-sub-multi-price-insert", el =>
		setPriceStr(el, "beast-yearly", true)
	);
	$each(document, "span.yearly-sub-multi-price-insert-rounded", el =>
		setPriceStr(el, "beast-yearly", true, false, false, true)
	);
	$each(document, "span.yearly-sub-base-plus-multi-price-insert", el =>
		setPriceStr(el, "beast-yearly-plus-multi", true)
	);
	$each(document, "span.yearly-sub-equivalent-monthly-price-insert", el =>
		setPriceStr(el, "beast-yearly", false, true)
	);
	$each(document, "span.yearly-sub-multi-equivalent-monthly-price-insert", el =>
		setPriceStr(el, "beast-yearly", true, true)
	);
	$each(document, "span.bundle-price-insert", el => setPriceStr(el, "bundle"));
	$each(document, "span.bundle-price-insert-dollars", el =>
		setPriceStr(el, "bundle", false, false, "dollars")
	);
	$each(document, "span.bundle-price-insert-cents", el =>
		setPriceStr(el, "bundle", false, false, "cents")
	);
	$each(document, "span.bundle-undiscounted-price-insert", el =>
		setPriceStr(el, "bundle-undiscounted")
	);
	$each(document, ".bundle-undiscounted-rounded-insert", el =>
		setPriceStr(el, "bundle-undiscounted", false, false, false, true)
	);
	$each(document, "span.bundle-minus-books-price-insert", el =>
		setPriceStr(el, "bundle-minus-books")
	);
	$each(document, "span.book-guide-practice-full-set-price-insert", el =>
		setPriceStr(el, "book-guide-practice-set")
	);
}

//
// JS for helper views (switchView, etc.)
//

// Used by bookCover, maybe others later.
function setupImageSwitchView(el) {
	const mainImg = el.querySelector(".ba11-img-switch-main");
	const fallbackImg = el.querySelector(".ba11-img-switch-fallback");
	if (fallbackImg)
		mainImg.addEventListener("load", function(evt) {
			fallbackImg.style.display = "none";
		});
}

function setupTabView(el) {
	// We look at the descendants of a querySelector call instead of finding all
	// ba-tab-sel-one/ba-tab-body-one elements so that nested tab controls will
	// work.
	const tabElContainer = el.querySelector(".ba11-tab-sel");
	const tabEls = Array.from(tabElContainer.children);
	const bodyElContainer = el.querySelector(".ba11-tab-body");
	const bodyEls = Array.from(bodyElContainer.children);

	let currentIndex = -1;

	tabEls.forEach(function(tabEl, i) {
		tabEl.tabIndex = 0;
		if (tabEl.classList.contains("--active")) {
			currentIndex = i;
		}
		const selectTab = function() {
			if (currentIndex === i) return;
			tabEls[currentIndex] &&
				toggleClass(tabEls[currentIndex], "--active", false);
			bodyEls[currentIndex] &&
				toggleClass(bodyEls[currentIndex], "--active", false);
			tabEls[i] && toggleClass(tabEls[i], "--active", true);
			bodyEls[i] && toggleClass(bodyEls[i], "--active", true);
			currentIndex = i;
		};
		tabEl.addEventListener("click", selectTab);
		tabEl.addEventListener("keypress", function(evt) {
			if (evt.keyCode === 13) {
				selectTab();
			}
		});
	});
}

function setupSwitchSwipe(el, slideFunc) {
	let xStart, yStart;

	const slideTouchMove = function(evt) {
		if (!xStart || !yStart) {
			return;
		}

		const xEnd = evt.changedTouches[0].pageX;
		const yEnd = evt.changedTouches[0].pageY;

		const xDiff = xStart - xEnd;
		const yDiff = yStart - yEnd;
		if (!yDiff && !xDiff) {
			return;
		}
		if (Math.abs(xDiff) > Math.abs(yDiff)) {
			evt.preventDefault();
			if (xDiff > 0) {
				// left swipe
				slideFunc(1);
			} else {
				// right swipe
				slideFunc(-1);
			}
		}
		/* reset values */
		xStart = null;
		yStart = null;
	};

	const slideTouchEnd = function() {
		document.removeEventListener("touchmove", slideTouchMove);
		document.removeEventListener("touchend", slideTouchEnd);
	};

	el.addEventListener("touchstart", function(evt) {
		xStart = evt.changedTouches[0].pageX;
		yStart = evt.changedTouches[0].pageY;
		document.addEventListener("touchmove", slideTouchMove, {
			passive: false,
		});
		document.addEventListener("touchend", slideTouchEnd);
	});

	el.addEventListener("touchend", function(evt) {
		slideTouchEnd();
	});
}

function setupSwitchView(el) {
	let selectedIndex = 0;

	const hiddenContainer = el.querySelector(".ba11-switch-content-hidden");
	const visContainer = el.querySelector(".ba11-switch-content-visible");
	const contentEntries = $find(hiddenContainer, ".ba11-switch-content-entry");

	const setSelectedIndex = function(newIndex) {
		selectedIndex = newIndex;
		buttons.forEach(function(b, i) {
			toggleClass(b, "--active", i === selectedIndex);
		});

		// TODO: transition
		const content = contentEntries[newIndex];
		visContainer.innerHTML = content ? content.innerHTML : "";
	};

	const setSelectedIndexRel = function(diff) {
		const n = contentEntries.length;
		let newIndex = selectedIndex + diff;
		// Clamp newIndex to 0 to n-1.
		newIndex = ((newIndex % n) + n) % n;
		setSelectedIndex(newIndex);
	};

	const buttons = $find(el, ".ba11-switch-circle-button-container");
	buttons.forEach(function(button, i) {
		button.addEventListener("click", function(evt) {
			setSelectedIndex(i);
		});
	});

	const prevButton = el.querySelector(".ba11-switch-previous");
	prevButton.addEventListener("click", function(evt) {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(-1);
	});

	const nextButton = el.querySelector(".ba11-switch-next");
	nextButton.addEventListener("click", function(evt) {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(1);
	});

	setSelectedIndex(0);
	setupSwitchSwipe(el, setSelectedIndexRel);
}

function setupSwitchQuoteView(el) {
	let selectedIndex = 0;

	const hiddenContainer = el.querySelector(".ba11-switch-quote-content-hidden");
	const visContainer = el.querySelector(".ba11-switch-quote-content-visible");
	const quotes = $find(hiddenContainer, ".ba11-switch-quote-content-entry");

	const setSelectedIndex = function(newIndex) {
		selectedIndex = newIndex;
		buttons.forEach(function(b, i) {
			toggleClass(b, "--active", i === selectedIndex);
		});

		// TODO: transition
		const quote = quotes[newIndex];
		visContainer.innerHTML = quote ? quote.innerHTML : "";
	};

	const setSelectedIndexRel = function(diff) {
		const n = quotes.length;
		let newIndex = selectedIndex + diff;
		// Clamp newIndex to 0 to n-1.
		newIndex = ((newIndex % n) + n) % n;
		setSelectedIndex(newIndex);
	};

	const buttons = $find(el, ".ba11-switch-quote-circle-button");
	buttons.forEach(function(button, i) {
		button.addEventListener("click", function(evt) {
			setSelectedIndex(i);
		});
	});

	const prevButton = el.querySelector(".ba11-switch-quote-previous");
	prevButton.addEventListener("click", function(evt) {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(-1);
	});

	const nextButton = el.querySelector(".ba11-switch-quote-next");
	nextButton.addEventListener("click", function(evt) {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(1);
	});

	setSelectedIndex(0);
	setupSwitchSwipe(el, setSelectedIndexRel);
}

function setupCarouselSwitchView(el) {
	let selectedIndex = 0;

	const slides = el.querySelectorAll(".ba11-carousel-switch-slide");
	const buttons = $find(el, ".ba11-carousel-switch-circle-button-container");
	buttons.forEach((button, i) => {
		button.addEventListener("click", evt => {
			setSelectedIndex(i, true);
		});
	});
	buttons.forEach((button, i) => {
		button.addEventListener("keydown", evt => {
			if (13 === evt.keyCode) setSelectedIndex(i, true);
		});
	});

	const setSelectedIndex = function(newIndex, announce, setFocusToSlide) {
		selectedIndex = newIndex;
		buttons.forEach((button, i) => {
			toggleClass(button, "--active", i === selectedIndex);
		});
		slides.forEach((slide, i) => {
			toggleClass(slide, "selected", i === selectedIndex);
			const focusableElements = getFocusableElements(slide);
			for (let j = 0; j < focusableElements.length; j++) {
				const tabIndex = i === selectedIndex ? 0 : 1;
				focusableElements[j].setAttribute("tabindex", tabIndex);
			}
			toggleAttribute(slide, "aria-hidden", i !== selectedIndex);
		});
		// TODO: set focus to slide contents when user clicks on of the buttons to select a particular slide
		if (announce) {
			el.querySelector(
				".ba11-carousel-switch-liveregion"
			).textContent = `Item ${newIndex + 1} of ${slides.length}`;
		}
	};

	const setSelectedIndexRel = function(diff) {
		const n = slides.length;
		let newIndex = selectedIndex + diff;
		// Clamp newIndex to 0 to n-1.
		newIndex = ((newIndex % n) + n) % n;
		setSelectedIndex(newIndex, true);
	};

	const prevButton = el.querySelector(".ba11-carousel-switch-previous");
	prevButton.addEventListener("click", evt => {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(-1);
	});

	const nextButton = el.querySelector(".ba11-carousel-switch-next");
	nextButton.addEventListener("click", evt => {
		// TODO: also force transition to be certain direction
		setSelectedIndexRel(1);
	});

	setSelectedIndex(0);
	setupSwitchSwipe(el, setSelectedIndexRel);
}

//
// JS for FAQ (expandables can be moved out of this section once we have more)
//

function trackQuestionOpen(el) {
	const questionEl = el.querySelector(".ba11-expandable-title");
	const questionText = questionEl.innerText;
	if (!global.ga) return;
	const fields = {
		hitType: "event",
		eventCategory: "FAQ Interaction",
		eventAction: "faq-click",
		eventLabel: questionText,
	};
	global.ga("send", fields);
}

function setupExpandable(el) {
	const headerToggle = el.querySelector(".ba11-expandable-toggle-el");
	const openCallback = hasClass(el, "ba11-p-faq-expandable")
		? trackQuestionOpen
		: null;
	setupToggleOn(el, headerToggle, {openCallback});
}

function setupFAQAnchor() {
	const urlPath = window.location.pathname;
	const urlMatch = /^\/faq\/([^\/]+)\/([^\/]+)$/.exec(urlPath);
	const questionName = urlMatch && urlMatch[2];

	const questionEl = document.getElementById(questionName);
	if (questionEl && hasClass(questionEl, "ba11-expandable")) {
		trackQuestionOpen(questionEl);
		scrollToEl(questionEl);
		toggleExpandableOpen(questionEl);
	}
}

function setupResourcesAnchor() {
	const urlPath = window.location.pathname;
	const urlMatch = /^\/resources\/([^\/]+)\/([^\/]+)$/.exec(urlPath);
	let expandableName = urlMatch && urlMatch[2] && urlMatch[2].toLowerCase();

	if (expandableName)
		expandableName = "ba11-resources-expandable-" + expandableName;

	const expandableEl = document.getElementById(expandableName);
	if (expandableEl && hasClass(expandableEl, "ba11-expandable")) {
		scrollToEl(expandableEl);
		toggleExpandableOpen(expandableEl, true);
	}
}

function setupExpandableDirectLinks(el) {
	const directLinkTo = el.getAttribute("data-direct-link");
	onClickOrEnter(el, () => {
		openModal(
			{
				title: "Direct Link",
				contents: `<input class="ba11-direct-link-input" type="text" value="https://beastacademy.com${directLinkTo}" readonly />`,
			},
			el
		);
	});
}

//
// JS for apparel page
//

function getItemsForApparel(apparelType, size, quantity) {
	return [
		{
			sku: apparelType + "-" + size,
			quantity,
			options: {
				apparelHash: "shirt-beast",
				size: "youth-small",
			},
		},
	];
}

function setupApparelPage(el) {
	// Image selection
	const images = $find(el, ".ba11-p-apparel-overview-image");
	const imageSelectorButtons = $find(
		el,
		".ba11-p-apparel-overview-image-selector"
	);
	const selectImage = evt => {
		const selectedEl = evt.target;
		imageSelectorButtons.forEach(button =>
			toggleClass(button, "--unselected", selectedEl !== button)
		);
		const imageToShowClass = selectedEl.getAttribute("data-image-type");
		images.forEach(image => {
			toggleClass(image, "ba11--hidden", !hasClass(image, imageToShowClass));
		});
	};
	imageSelectorButtons.forEach(b => onClickOrEnter(b, selectImage));

	// Update based on item/quantity selection

	const dropdown = el.querySelector(".ba11-p-apparel-overview-buy-dropdown");
	const apparelHash = dropdown.getAttribute("data-apparel-hash");
	const qtyInput = el.querySelector(".ba11-p-apparel-overview-buy-qty-sel");
	const addButton = el.querySelector(".ba11-p-apparel-overview-buy-button");
	const fullPriceEl = el.querySelector(
		".ba11-p-apparel-overview-buy-full-price"
	);
	const priceEl = el.querySelector(".ba11-p-apparel-overview-buy-price");

	const getCurrentItems = function() {
		const size = dropdown.value;
		const quantity = parseInt(qtyInput.value) || 1;
		const itemsToAdd = getItemsForApparel(apparelHash, size, quantity);
		return itemsToAdd;
	};

	const updateShownPrice = function() {
		const items = getCurrentItems();
		const total = ElevPrices.calculateTotalItemPrice(items);
		const totalFull = ElevPrices.calculateTotalItemPrice(items, {
			skipDiscounts: true,
		});
		priceEl.innerHTML = "$" + total.toFixed(2);
		if (totalFull > total) {
			fullPriceEl.innerHTML = "$" + totalFull.toFixed(2);
			toggleClass(fullPriceEl, "--hidden", false);
		}
	};

	qtyInput.addEventListener("change", function(evt) {
		updateShownPrice();
	});

	onClickOrEnter(addButton, evt => {
		const itemsToAdd = getCurrentItems();
		modifyCart(c => {
			addItemsToCart(itemsToAdd, c);
		});
		showAddToCartModal(getTotalQuantity(itemsToAdd), "shirts", addButton);
	});

	updateShownPrice();
}

//
// Special URL handling
//

function checkMailingListURL() {
	if (window.location.pathname === "/mailingList") {
		setTimeout(() => {
			window.scrollTo(0, document.body.scrollHeight);
			const emailSignUpButton = document.querySelector(
				".ba11-email-signup-arrow-button"
			);
			if (emailSignUpButton) emailSignUpButton.click();
		}, 500);
	}
}

//
// Unsupported Browser Modal
//

function detectUnsupportedBrowser() {
	const ua = window.navigator.userAgent;
	const msie = ua.indexOf("MSIE ") > -1; // IE10 or older
	const trident = ua.indexOf("Trident/") > -1; // IE11

	if (msie || trident) {
		openModalByName("unsupportedBrowserIE");
		return true;
	}
}

function setUpEnrollNowButtons(user) {
	// All enroll buttons that go to /online/enroll and subscription cards
	const enrollButtons = document.querySelectorAll(
		'a[href="/online/enroll"], .subscription-card:not(#vc-modal-open-btn)'
	);

	// if the user is logged in but ALSO in a CW session, send them to the
	// original /online/enroll page since they cannot use CW from within their account
	const isClassWalletFlow = getCookie("cw");
	if (enrollButtons.length > 0 && user && !isClassWalletFlow) {
		enrollButtons.forEach(button => {
			// so the link when you hover shows /account/add/subscribe
			button.href = "/account/add/subscribe";
			button.addEventListener("click", e => {
				e.preventDefault();
				e.stopPropagation();
				window.location.href = "/account/add/subscribe";
			});
		});
	} else if (!user) {
		// if the user is not logged in, track the click event
		enrollButtons.forEach(button => {
			button.addEventListener("click", e => {
				const eventProperties = {};
				if (button.id) {
					eventProperties.target = button.id;
				} else if (button.classList.contains("subscription-card")) {
					eventProperties.target = "subscription-card-cta";
				}
				track("Enroll Button Clicked", {
					...eventProperties,
				});
			});
		});
	}
}

function setupCopyrightText(el) {
	const d = new Date();
	const year = d.getFullYear();

	el.innerHTML = `© <span id="copyright-year">${year}</span> AoPS Incorporated`;
}

//
// GA4 / Analytics
//

function sendDataLayerEvents() {
	if (window.pushToDataLayer) {
		// Single Book Page View Item
		const singleBookContainerEl = document.querySelector(
			".ba11-p-booksingle-container"
		);
		if (singleBookContainerEl) {
			const bookHash = singleBookContainerEl.getAttribute("data-book-hash");
			// Defaults to the either the guide book or the puzzle book
			let sku = "beast-";
			if (/^\d[A-D]$/.test(bookHash)) {
				sku += bookHash.toLowerCase();
			} else if (/^puzzles\d$/.test(bookHash)) {
				sku += bookHash.substr(7, 1) + "-puzzles";
			}

			sendDataLayerEvent("view_item", {
				// GA4
				items: [
					{
						item_id: sku,
						item_category: "book",
						price: ElevPrices.getBaseItemPriceForSku(sku),
						currency: "USD",
					},
				],
				// FB
				contents: [
					{
						id: sku,
						price: ElevPrices.getBaseItemPriceForSku(sku),
						currency: "USD",
						quantity: 1,
					},
				],
				content_ids: [sku],
			});
		}

		// Apparel
		const apparelContainerEl = document.querySelector(
			".ba11-p-apparel-container"
		);
		if (apparelContainerEl) {
			// Defaults to youth small
			const sku = "shirt-beast-youth-small";
			sendDataLayerEvent("view_item", {
				// GA4
				items: [
					{
						item_id: sku,
						item_category: "apparel",
						price: ElevPrices.getBaseItemPriceForSku(sku),
						currency: "USD",
					},
				],
				// FB
				contents: [
					{
						id: sku,
						price: ElevPrices.getBaseItemPriceForSku(sku),
						currency: "USD",
						quantity: 1,
					},
				],
				content_ids: [sku],
			});
		}

		// Enroll views (/online/enroll)
		sendViewItemListEvent(".gtm-is-enroll", [
			{item_id: "beast-monthly"},
			{item_id: "beast-yearly"},
			{item_id: "beast-bundle"},
		]);
		// Book level bundle views (/books/level-X)
		const levelBundle = "[data-gtm-book-level]";
		let lvl = document.querySelector(levelBundle);
		if (lvl) {
			lvl = lvl.attributes.getNamedItem(levelBundle.slice(1, -1)).value;
			const books = [];
			const units = "abcd";

			for (const char of units) {
				books.push({item_id: "beast-" + lvl + char});
				if (lvl !== "1") {
					// Level 1 books are practice and guidebook combos
					books.push({item_id: "beast-" + lvl + char + "-practice"});
				}
			}

			sendViewItemListEvent(levelBundle, books);
		}
	}
}

/**
 * Send a view_item_list GA4 event to the dataLayer, call within sendDataLayerEvents()
 * @param {string} sel The unique selector on the page that confirms if the event should fire
 * @param {array} skus An array of objects containing the SKU for the products in the list
 */
const sendViewItemListEvent = function(sel, skus) {
	if (document.querySelector(sel)) {
		const items = [];
		const contents = [];
		skus.forEach(function(sku) {
			// Get price & category from shared/data/dbPrices.json
			// GA4
			items.push({
				item_id: sku.item_id,
				price: ElevPrices.getBaseItemPriceForSku(sku.item_id),
				item_category: ElevPrices.getTypeForSku(sku.item_id),
				currency: "USD",
			});
			// FB
			contents.push({
				id: sku.item_id,
				price: ElevPrices.getBaseItemPriceForSku(sku.item_id),
				quantity: 1,
				currency: "USD",
			});
		});

		// The view_item_list differs from view_item because the latter
		// expects any arbitrary combination of items vs the former which
		// expects a group of items advertised together (eg. bundles)
		sendDataLayerEvent("view_item_list", {
			items: items,
			contents: contents,
			content_ids: skus.map(sku => sku.item_id),
		});
	}
};

// Set up the cart for ClassWallet
// If this is a ClassWallet session, there will be a "cw" cookie set
// We check for the presence of that cookie. If it is present, we will
// grab the current cart (or the default cart if there is none) and provide
// that to the saveCart endpoint so that it can create a row in school_classwallet
// and return us a new cartID
const setupClassWallet = function() {
	// Get the cart and ClassWallet cookie
	const currentCart = loadCartFromStorage();
	const cwSessionID = getCookie("cw");

	// If there is a CW cookie, then we presume they're trying to shop
	// with ClassWallet. Show the ClassWallet icons and get a cartID
	// from the backend
	if (cwSessionID) {
		// If there is an existing cart, pass that cart to the backend
		// otherwise pass an empty cart
		const cart = currentCart.cartID ? currentCart : getDefaultCart();
		runAjax(
			{
				endpoint: "/api/store/saveCart",
				payload: {
					cart,
					cwSessionID,
				},
				timeout: AJAX_TIMEOUT,
			},
			(err, res) => {
				if (res.success) {
					// If successful, update cart in local storage with the cartID
					cart.cartID = res.cartID;
					saveCartToStorage(cart);
				}
			}
		);
		// Un-hide the ClassWallet icons in the top bar
		$each(document, "div.for-classwallet", el => {
			toggleClass(el, "for-classwallet", false);
		});
		// Remove bundles from options in book buy
		setupBookBuyForClassWallet();
		// in CW, let users know that live-classes cannot be purchased
		const liveClassesErrorModal = $find(
			document,
			".modal-wrapper.live-classes-error"
		)[0];
		disableLiveClassesLinks(liveClassesErrorModal);
	}
};

const disableAnalyticsIfStudent = function(user) {
	if (user && (user.roles || []).includes("student")) {
		disableAnalytics();
	}
};

const disableAnalytics = function() {
	if (global.pushToDataLayer) {
		// GTM will listen to this variable and do the right thing to stop
		// tracking even though subsequent data layer events are sent.
		global.pushToDataLayer({stopTracking: true});
	} else {
		// should never run, as we always initialize the data layer
		// some extra safety if we refactor
		setTimeout(1000, disableAnalytics);
	}
};

/**
 * Sends a variable to the tag manager (currently Google Tag Manager) to tell
 * it if the user is logged in or not. This helps our data & marketing teams to
 * determine how the website performs between new & returning users.
 *
 * See also _/views/layout/main.hbs_ for the React side of this.
 */
const tellTagManagerIfTheUserIsLoggedIn = function(user) {
	/**
	 * `user` is defined when I log in. Correct me if I'm wrong, this seems
	 * like a valid way to check login status.
	 */
	const isUserLoggedIn = !!user;
	const gtmVar = {isUserLoggedIn};
	/* `sendDataLayerEvent()` doesn't push objects to the `dataLayer` in test
	environments but I think it should so that we can test if it's sending to
	the GTM test container instead of failing to send. Also,
	don't bother logging anything on test environments because
	https://tagassistant.google.com/ tells us everything we need to know. */
	global.dataLayer.push(gtmVar);
};

//
// Entry point
//

document.addEventListener("DOMContentLoaded", function() {
	if (detectUnsupportedBrowser()) return;

	initSubscribe();
	logMarketingSource();

	$each(document, ".ba11-carousel-container", setupCarousel);
	$each(document, ".ba11-img-switch", setupImageSwitchView);
	$each(document, ".ba11-tab-container", setupTabView);
	$each(document, ".ba11-switch-main", setupSwitchView);
	$each(document, ".ba11-switch-quote-main", setupSwitchQuoteView);
	$each(document, ".ba11-carousel-switch", setupCarouselSwitchView);
	$each(document, ".ba11-p-booksingle-overview-buy", setupBookBuy);
	$each(document, ".product-top-section", setupBookBuy);
	$each(document, ".ba11-expandable", setupExpandable);
	$each(document, ".ba11-p-enroll-contents", setupEnrollPage);
	$each(document, ".ba11-p-apparel-container", setupApparelPage);
	$each(document, ".ba11-p-contact-container", setupContactUsPage);
	$each(document, ".ba11-modal-button", el => setupModalButton(el));
	$each(document, ".ba11-expandable-direct-link", setupExpandableDirectLinks);
	$each(document, ".ba11-bottom-bar-copyright", setupCopyrightText);
	fillInPrices();
	setupFAQAnchor();
	setupResourcesAnchor();
	setupAllBooks();
	setupSaveWithFriends();
	setupGiftingPage();
	setupPlayground();
	setupOutcomesPage();

	checkMailingListURL();

	sendDataLayerEvents();

	runAjaxAuthenticate((err, res) => {
		const user = res && res.user;
		const Mixpanel = require("../../../core/util/Mixpanel");
		Mixpanel.init();

		if (user) {
			Mixpanel.setUser(user);
		}

		tellTagManagerIfTheUserIsLoggedIn(user);
		setupTopBar(user);
		setupTopBarPostAuth(user);
		setupBookBuyPostAuth(user);
		setupContactPagePostAuth(user);
		setupImitatorIndicator(user);
		disableAnalyticsIfStudent(user);
		setUpEnrollNowButtons(user);
	});
	setupClassWallet();
});
